import { ReactElement, useEffect, useState } from "react";
import { Box, Grid } from "mui";
import loginImage from "assets/images/login.png";
import verificationImage from "assets/images/verification.png";
import LoginForm from "modules/Auth/components/LogInForm/LoginForm";
import VerificationForm from "modules/Auth/components/VerificationForm/VerificationForm";
import AuthenticationForm from "modules/Auth/components/AuthenticationForm/AuthenticationForm";
import styles from "modules/Auth/views/Login.module.scss";
import { useAppDispatch, useAppSelector } from "utils/hooks/storeHooks";
import {
  selectLogInStatus,
  selectLoginRequestStatus,
  selectTwoFactorValidated,
} from "store/session/selectors";
import { SessionStatus } from "types/Session";
import { useNavigate } from "react-router";
import {
  clearSavedCredentials,
  logOut,
  setLoginRequestStatus,
  setTwoFactorValidated,
} from "store/session";
import { useTabClose } from "utils/hooks/useTabClose";
import { LoginStep } from "constants/loginSteps";
import { RequestStatus as Status } from "constants/RequestStatus";
import Toast from "components/Toast/Toast";
import { LoginToastMessages } from "constants/Login";

interface Step {
  class: string;
  image: string;
  component: ReactElement;
}

const Login = (): ReactElement => {
  const [step, setNextStep] = useState(LoginStep.Login);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const loginStatus = useAppSelector(selectLogInStatus);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState(LoginToastMessages.loginError);
  const twoFactorValidated = useAppSelector(selectTwoFactorValidated);
  const loginRequestStatus = useAppSelector(selectLoginRequestStatus);
  const handleSubmit = (newStep: LoginStep) => {
    if (newStep !== "end") {
      setNextStep(newStep);
    }
  };
  const handleTabClose = (event?: BeforeUnloadEvent) => {
    event?.preventDefault();
    setNextStep(LoginStep.Login);
    clearSavedCredentials();
    dispatch(logOut());
  };

  useEffect(() => {
    if (
      step === LoginStep.Login &&
      loginStatus === SessionStatus.waitingTwoFactorAuth
    ) {
      setNextStep(LoginStep.Authentication);
    }
    if (loginStatus === SessionStatus.fulfilled) {
      navigate("/");
    }
  }, [dispatch, loginStatus, step]);

  useEffect(() => {
    if (
      twoFactorValidated === Status.Failed &&
      loginRequestStatus === Status.Failed
    ) {
      setToastMessage(LoginToastMessages.twoFactorAuthError);
      setShowToast(true);
    }
    if (
      loginRequestStatus === Status.Failed &&
      twoFactorValidated !== Status.Failed
    ) {
      setToastMessage(LoginToastMessages.loginError);
      setShowToast(true);
    }
  }, [twoFactorValidated, loginRequestStatus]);

  useTabClose(handleTabClose);

  const steps: { [key: string]: Step } = {
    login: {
      image: loginImage,
      component: <LoginForm onSend={handleSubmit} />,
      class: "loginSection",
    },
    verification: {
      image: verificationImage,
      component: <VerificationForm onSend={handleSubmit} />,
      class: "verificationSection",
    },
    authentication: {
      image: verificationImage,
      component: (
        <AuthenticationForm onSend={handleSubmit} onGoBack={handleTabClose} />
      ),
      class: "verificationSection",
    },
  };

  return (
    <Box className={styles.background}>
      <Toast
        key="toast-login"
        message={toastMessage.message}
        open={showToast}
        variant={toastMessage.variant}
        onClose={() => {
          dispatch(setLoginRequestStatus(Status.Idle));
          dispatch(setTwoFactorValidated(Status.Idle));
          setShowToast(false);
        }}
      />
      <Grid className={styles.generalContainer} container>
        <Grid className={styles.formContainer} item md={6} xs={12}>
          <img className={styles.headerImage} src={loginImage} alt="Login" />
          <Box className={styles[steps[step].class]}>
            <Box>{steps[step].component}</Box>
          </Box>
        </Grid>
        <Grid className={styles.imageSection} item md={6}>
          <img src={steps[step].image} alt="Login" />
        </Grid>
      </Grid>
    </Box>
  );
};

export default Login;
